import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import Select from "react-select";
import styled, { css, keyframes } from "styled-components";
import { EMAIL_REGEX, MOBILE_REGEX } from "../../../../../utils/Constants";
import { COUNTRY_LIST, getCountryByCode, getCountryByName } from "../../../../../utils/CountryList";
import { GetAccountPayerDetails, UpdateAccountPayer, getReRegistrationService } from "../../../../../services/ReRegistrationService";
import ApplicationLayout from "../layout/ApplicationLayout";
import { Container, SectionContainer, SectionTitle, SectionDescription, ButtonDoneEditing } from "../styles/primitives";
import PaymentMethods from "../../../SupportingDocuments/PaymentMethods";

const AccountPayerOptions = [
  { value: -1, label: "Individual" },
  { value: 2, label: "Parent or Guardian" },
  { value: 3, label: "Same as Student" },
  { value: 8, label: "Company" },
  { value: 7, label: "Other" },
];

const PaymentOptionOptions = [
  { value: "Full Payment", label: "Full Payment" },
  { value: "Installments", label: "Installments" },
  { value: "Pay 50 percent now and 50 percent later", label: "Pay 50 percent now and 50 percent later" },
];

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const loadingSurface = css`
  background: linear-gradient(
    90deg,
    #eef1f5 8%,
    #e8ecf2 20%,
    #eef1f5 35%
  );
  background-size: 180% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

const FormLoadingWrapper = styled.div`
  position: relative;
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      pointer-events: none;
      & .form-control,
      & .custom-select-box,
      & .acc-payer,
      & .custom-control-label:before {
        ${loadingSurface};
        color: transparent;
      }
    `}
`;

const createValidationSchema = (isRequired) => {
  return Yup.object({
    custentity_aie_nature_of_sponsor: isRequired
      ? Yup.number()
          .typeError("Please select an account payer type")
          .min(-1, "Please select a valid account payer type")
          .max(15, "Please select a valid account payer type")
          .required("Please select an account payer type")
      : Yup.number()
          .typeError("Please select an account payer type")
          .min(-1, "Please select a valid account payer type")
          .max(15, "Please select a valid account payer type")
          .nullable(),
    firstname: isRequired
      ? Yup.string()
          .max(100, "First name cannot exceed 100 characters")
          .required("First name is required")
      : Yup.string()
          .max(100, "First name cannot exceed 100 characters")
          .nullable(),
    lastname: isRequired
      ? Yup.string()
          .max(100, "Last name cannot exceed 100 characters")
          .required("Last name is required")
      : Yup.string()
          .max(100, "Last name cannot exceed 100 characters")
          .nullable(),
    custentity_bb1_contact_id_num: Yup.string()
      .max(50, "ID or passport number cannot exceed 50 characters")
      .nullable(),
    email: isRequired
      ? Yup.string()
          .email("Please enter a valid email address")
          .matches(EMAIL_REGEX, "Please enter a valid email address")
          .max(255, "Email address cannot exceed 255 characters")
          .required("Email address is required")
      : Yup.string()
          .email("Please enter a valid email address")
          .matches(EMAIL_REGEX, "Please enter a valid email address")
          .max(255, "Email address cannot exceed 255 characters")
          .nullable(),
    phone: isRequired
      ? Yup.string()
          .matches(MOBILE_REGEX, "Please enter a valid phone number")
          .max(100, "Phone number cannot exceed 100 characters")
          .required("Phone number is required")
      : Yup.string()
          .matches(MOBILE_REGEX, "Please enter a valid phone number")
          .max(100, "Phone number cannot exceed 100 characters")
          .nullable(),
    billingaddr1: isRequired
      ? Yup.string()
          .max(500, "Billing address cannot exceed 500 characters")
          .required("Billing address is required")
      : Yup.string()
          .max(500, "Billing address cannot exceed 500 characters")
          .nullable(),
    billingcity: isRequired
      ? Yup.string()
          .max(100, "City cannot exceed 100 characters")
          .required("City is required")
      : Yup.string()
          .max(100, "City cannot exceed 100 characters")
          .nullable(),
    billingzip: isRequired
      ? Yup.string()
          .max(20, "Postal code cannot exceed 20 characters")
          .required("Postal code is required")
      : Yup.string()
          .max(20, "Postal code cannot exceed 20 characters")
          .nullable(),
    billingcountry: isRequired
      ? Yup.string()
          .max(100, "Please select a valid country")
          .required("Country is required")
      : Yup.string()
          .max(100, "Please select a valid country")
          .nullable(),
    custentity_aie_le_name: Yup.string()
      .max(255, "Company name cannot exceed 255 characters")
      .when("custentity_aie_nature_of_sponsor", {
        is: 8,
        then: (schema) =>
          isRequired
            ? schema.required("Company name is required")
            : schema.nullable(),
        otherwise: (schema) => schema.nullable(),
      }),
    custentity_aie_le_reg_nr: Yup.string()
      .max(255, "Company registration number cannot exceed 255 characters")
      .when("custentity_aie_nature_of_sponsor", {
        is: 8,
        then: (schema) =>
          isRequired
            ? schema.required("Company registration number is required")
            : schema.nullable(),
        otherwise: (schema) => schema.nullable(),
      }),
    custentity_aie_le_representative: Yup.string()
      .max(255, "Company representative name cannot exceed 255 characters")
      .when("custentity_aie_nature_of_sponsor", {
        is: 8,
        then: (schema) =>
          isRequired
            ? schema.required("Company representative is required")
            : schema.nullable(),
        otherwise: (schema) => schema.nullable(),
      }),
  });
};

export default function AccountPayerChangeStep({
  onSubmitted,
  locked = false,
  preloadedAccountPayer = null,
  studentRegistrationId = null,
  sponsorPaymentMethod = null,
  registrationId = null,
  accountPayerUpdated = 0,
}) {
  const user = useSelector((state) => state.user);
  const [initialPayer, setInitialPayer] = useState({});
  const [editEnabled, setEditEnabled] = useState(true);
  const [currentSponsorPaymentMethod, setCurrentSponsorPaymentMethod] = useState(sponsorPaymentMethod);
  const [noSponsorData, setNoSponsorData] = useState(false);
  const [noCustomer, setNoCustomer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(preloadedAccountPayer === null);
  const userEditToggleRef = useRef(false);

  const userId = useMemo(() => {
    return user?.id || "";
  }, [user]);

  const studentNumber = useMemo(() => {
    return user?.student_crm_id || user?.student_id || (() => {
      try {
        const userData = JSON.parse(localStorage.getItem("user")) || {};
        return userData?.student_crm_id || userData?.student_id || "";
      } catch {
        return "";
      }
    })();
  }, [user]);

  const normalizeCountryInput = (countryValue) => {
    const value = countryValue || "ZA";
    const country = getCountryByCode(value) || getCountryByName(value);
    return country ? country.value : value;
  };

  const applicantProfile = useMemo(() => {
    return {
      firstName: user?.first_name || "",
      lastName: user?.last_name || "",
      idNumber: user?.number || "",
      email: user?.email || "",
      phone: user?.mobile || user?.phone || "",
      address: user?.ph_street || "",
      city: user?.ph_city || "",
      postalCode: user?.ph_zip || "",
      country: normalizeCountryInput(user?.ph_country || "ZA"),
    };
  }, [user]);

  // Use preloaded data if available, otherwise fetch it
  useEffect(() => {
    userEditToggleRef.current = false;
    const setEditEnabledWithGuard = (value) => {
      setEditEnabled((prev) => (userEditToggleRef.current ? prev : value));
    };
    
    if (preloadedAccountPayer !== null) {
      const data = preloadedAccountPayer;
      if (data?.noCustomer) {
        setNoCustomer(true);
        setInitialPayer({});
        setNoSponsorData(false);
        setEditEnabledWithGuard(true);
      } else {
        setNoCustomer(false);
        setInitialPayer(data || {});
        if (data?.noSponsorData) {
          setNoSponsorData(true);
          setEditEnabledWithGuard(true);
        } else {
          setNoSponsorData(false);
          setEditEnabledWithGuard(false);
        }
      }
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    if (!userId) {
      setInitialPayer({});
      setNoSponsorData(true);
      setEditEnabledWithGuard(true);
      setIsLoading(false);
      return;
    }

    GetAccountPayerDetails(userId)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data[0] : res.data || {};
        if (data?.noCustomer) {
          setNoCustomer(true);
          setInitialPayer({});
          setNoSponsorData(false);
          setEditEnabledWithGuard(true);
        } else {
          setNoCustomer(false);
          setInitialPayer(data || {});
          if (data?.noSponsorData) {
            setNoSponsorData(true);
            setEditEnabledWithGuard(true);
          } else {
            setNoSponsorData(false);
            setEditEnabledWithGuard(false);
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setInitialPayer({});
        setNoSponsorData(true);
        setNoCustomer(false);
        setEditEnabledWithGuard(true);
        setIsLoading(false);
      });
  }, [preloadedAccountPayer, userId]);

  // Update currentSponsorPaymentMethod when prop changes
  useEffect(() => {
    if (sponsorPaymentMethod !== null && sponsorPaymentMethod !== undefined) {
      setCurrentSponsorPaymentMethod(sponsorPaymentMethod);
    }
  }, [sponsorPaymentMethod]);

  const [accountPayerUpdatedStatus, setAccountPayerUpdatedStatus] = useState(accountPayerUpdated);

  // Update accountPayerUpdatedStatus when prop changes
  useEffect(() => {
    setAccountPayerUpdatedStatus(accountPayerUpdated);
  }, [accountPayerUpdated]);

  // Fetch latest registration data on component load
  useEffect(() => {
    if (!registrationId) {
      return;
    }

    getReRegistrationService(registrationId)
      .then((res) => {
        const registrationData = res.data?.result || res.data || {};
        if (registrationData?.sponsor_payment_method) {
          setCurrentSponsorPaymentMethod(registrationData.sponsor_payment_method);
        }
        if (registrationData?.account_payer_updated !== undefined) {
          setAccountPayerUpdatedStatus(Number(registrationData.account_payer_updated || 0));
        }
      })
      .catch((error) => {
        console.error("Error fetching registration data:", error);
      });
  }, [registrationId]);

  const normalizeCountryValue = useMemo(() => {
    return normalizeCountryInput(initialPayer?.billingcountry || applicantProfile.country || "ZA");
  }, [initialPayer, applicantProfile]);

  const validationSchema = useMemo(() => {
    return createValidationSchema(noCustomer);
  }, [noCustomer]);

  const initialAccountPayerType = useMemo(() => {
    const value = initialPayer?.custentity_aie_nature_of_sponsor;
    if (value === undefined || value === null || value === "") return null;
    return Number(value);
  }, [initialPayer]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      custentity_aie_nature_of_sponsor:
        initialPayer?.custentity_aie_nature_of_sponsor !== undefined &&
        initialPayer?.custentity_aie_nature_of_sponsor !== null &&
        initialPayer?.custentity_aie_nature_of_sponsor !== ""
          ? Number(initialPayer?.custentity_aie_nature_of_sponsor)
          : 3,
      firstname: initialPayer?.firstname || applicantProfile.firstName || "",
      lastname: initialPayer?.lastname || applicantProfile.lastName || "",
      custentity_bb1_contact_id_num: initialPayer?.custentity_bb1_contact_id_num || applicantProfile.idNumber || "",
      email: initialPayer?.email || applicantProfile.email || "",
      phone: initialPayer?.phone || applicantProfile.phone || "",
      billingaddr1: initialPayer?.billingaddr1 || applicantProfile.address || "",
      billingcity: initialPayer?.billingcity || applicantProfile.city || "",
      billingzip: initialPayer?.billingzip || applicantProfile.postalCode || "",
      billingcountry: normalizeCountryValue,
      custentity_aie_le_name: initialPayer?.custentity_aie_le_name || "",
      payment_option:
        currentSponsorPaymentMethod !== undefined &&
        currentSponsorPaymentMethod !== null &&
        currentSponsorPaymentMethod !== ""
          ? currentSponsorPaymentMethod
          : "",
      custentity_aie_le_reg_nr: initialPayer?.custentity_aie_le_reg_nr || "",
      custentity_aie_le_representative: initialPayer?.custentity_aie_le_representative || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!userId) {
        Swal.fire({
          icon: "error",
          title: "Missing user ID",
          text: "We could not determine your user ID. Please contact support.",
        });
        return;
      }

      Swal.fire({ 
        title: "Updating Account Payer Details", 
        text: "Please wait while we save your account payer information. This may take a few moments...",
        allowEscapeKey: false, 
        allowOutsideClick: false, 
        onOpen: () => { 
          Swal.showLoading(); 
        }, 
      });
      setIsSubmitting(true);

      const dontChangeSponsor = editEnabled ? 0 : 1;
      const payload = {
        student_id: userId,
        student_registration_id: studentRegistrationId,
        billingaddr1: values.billingaddr1,
        billingcity: values.billingcity,
        billingcountry: values.billingcountry,
        billingzip: values.billingzip,
        custentity_bb1_contact_id_num: values.custentity_bb1_contact_id_num || "",
        email: values.email,
        firstname: values.firstname,
        lastname: values.lastname,
        phone: values.phone,
        custentity_aie_le_name: values.custentity_aie_le_name || "",
        custentity_aie_le_reg_nr: values.custentity_aie_le_reg_nr || "",
        custentity_aie_le_representative: values.custentity_aie_le_representative || "",
        custentity_aie_nature_of_sponsor: Number(values.custentity_aie_nature_of_sponsor),
        account_payer: Number(values.custentity_aie_nature_of_sponsor),
        dontChangeSponsor,
        paymentMethod: values.payment_option,
      };

      try {
        await UpdateAccountPayer(payload);
        Swal.close();
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: "Account payer details updated successfully.",
        });
        onSubmitted && onSubmitted({ updated: true });
      } catch (err) {
        Swal.close();
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: err?.response?.data?.[0]?.message || err?.message || "An error occurred",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    validateForm,
  } = formik;

  const resetPayerFieldsToInitial = () => {
    setFieldValue("firstname", initialPayer?.firstname || applicantProfile.firstName || "");
    setFieldValue("lastname", initialPayer?.lastname || applicantProfile.lastName || "");
    setFieldValue(
      "custentity_bb1_contact_id_num",
      initialPayer?.custentity_bb1_contact_id_num || applicantProfile.idNumber || ""
    );
    setFieldValue("email", initialPayer?.email || applicantProfile.email || "");
    setFieldValue("phone", initialPayer?.phone || applicantProfile.phone || "");
    setFieldValue("billingaddr1", initialPayer?.billingaddr1 || applicantProfile.address || "");
    setFieldValue("billingcity", initialPayer?.billingcity || applicantProfile.city || "");
    setFieldValue("billingzip", initialPayer?.billingzip || applicantProfile.postalCode || "");
    setFieldValue("billingcountry", normalizeCountryValue);
    setFieldValue("custentity_aie_le_name", initialPayer?.custentity_aie_le_name || "");
    setFieldValue("custentity_aie_le_reg_nr", initialPayer?.custentity_aie_le_reg_nr || "");
    setFieldValue(
      "custentity_aie_le_representative",
      initialPayer?.custentity_aie_le_representative || ""
    );
  };

  const clearPayerFields = () => {
    setFieldValue("firstname", "");
    setFieldValue("lastname", "");
    setFieldValue("custentity_bb1_contact_id_num", "");
    setFieldValue("email", "");
    setFieldValue("phone", "");
    setFieldValue("billingaddr1", "");
    setFieldValue("billingcity", "");
    setFieldValue("billingzip", "");
    setFieldValue("billingcountry", "");
    setFieldValue("custentity_aie_le_name", "");
    setFieldValue("custentity_aie_le_reg_nr", "");
    setFieldValue("custentity_aie_le_representative", "");
  };

  const applyApplicantProfileToForm = () => {
    setFieldValue("firstname", applicantProfile.firstName || "");
    setFieldValue("lastname", applicantProfile.lastName || "");
    setFieldValue("custentity_bb1_contact_id_num", applicantProfile.idNumber || "");
    setFieldValue("email", applicantProfile.email || "");
    setFieldValue("phone", applicantProfile.phone || "");
    setFieldValue("billingaddr1", applicantProfile.address || "");
    setFieldValue("billingcity", applicantProfile.city || "");
    setFieldValue("billingzip", applicantProfile.postalCode || "");
    setFieldValue("billingcountry", normalizeCountryInput(applicantProfile.country || "ZA"));
  };

  const resetAccountPayerToInitial = () => {
    if (initialAccountPayerType !== null) {
      setFieldValue("custentity_aie_nature_of_sponsor", initialAccountPayerType);
    }
    resetPayerFieldsToInitial();
  };

  const handleAccountPayerChange = (option) => {
    const newValue = option ? Number(option.value) : "";
    setFieldValue("custentity_aie_nature_of_sponsor", newValue);
    clearPayerFields();
    if (newValue === 3) {
      applyApplicantProfileToForm();
    }
  };

  const handleNoChangeToggle = (event) => {
    const isChecked = event.target.checked;
    userEditToggleRef.current = true;
    setEditEnabled(!isChecked);

    if (isChecked) {
      resetAccountPayerToInitial();
    } else if (Number(values.custentity_aie_nature_of_sponsor) === 3) {
      applyApplicantProfileToForm();
    }
  };

  useEffect(() => {
    validateForm();
  }, [noCustomer, validateForm]);

  const isEditable = (editEnabled || noSponsorData || noCustomer) && !isLoading;
  const isPaymentOptionEditable = !isLoading;
  const hasExistingAccountPayer = !isLoading && !noCustomer && !noSponsorData && initialPayer && Object.keys(initialPayer).length > 0;
  const shouldShowNoChangeToggle = hasExistingAccountPayer && !isLoading;

  const selectedAccountPayer = useMemo(() => {
    const value = values.custentity_aie_nature_of_sponsor;
    if (value === undefined || value === null || value === "") return null;
    const numericValue = Number(value);
    return AccountPayerOptions.find((opt) => opt.value === numericValue) || null;
  }, [values.custentity_aie_nature_of_sponsor]);

  const selectedCountry = useMemo(() => {
    const value = values.billingcountry;
    if (!value) return null;
    return COUNTRY_LIST.find((c) => c.value === value) || null;
  }, [values.billingcountry]);

  const selectedPaymentOption = useMemo(() => {
    const value = values.payment_option;
    if (!value) return null;
    return PaymentOptionOptions.find((opt) => opt.value === value) || null;
  }, [values.payment_option]);

  return (
    <Container>
      <ApplicationLayout>
        <SectionContainer>
          <SectionTitle>
            Confirm Account Payer Details
            {isLoading && <i className="fas fa-gear fa-spin" style={{ marginLeft: "8px", color: "var(--topbar-color)" }}></i>}
          </SectionTitle>
          <SectionDescription>
            {hasExistingAccountPayer
              ? "Please confirm the details of the account payer for the upcoming intake. Indicate below if the person responsible for your account has changed."
              : "Please provide the details of the account payer for the upcoming intake."}
          </SectionDescription>
          <FormLoadingWrapper className="card-body-inr" style={{ minWidth: "100%" }} $isLoading={isLoading}>
            {shouldShowNoChangeToggle && (
              <div className="d-flex align-items-center mb-3">
                <div className="custom-control custom-checkbox">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="pmc-payer-edit-toggle"
                    checked={!editEnabled}
                    onChange={handleNoChangeToggle}
                    disabled={isLoading}
                  />
                  <label className="custom-control-label" htmlFor="pmc-payer-edit-toggle">
                    My account payer details have not changed.
                  </label>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>

              
              <div className="row mb-2">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Account Payer <span className="required-asterisk">*</span></label>
                    <div className="form-icon-group">
                      <Select
                        className={
                          "form-control custom-select-box acc-payer" +
                          (errors.custentity_aie_nature_of_sponsor &&
                          touched.custentity_aie_nature_of_sponsor
                            ? " is-invalid"
                            : "")
                        }
                        name="custentity_aie_nature_of_sponsor"
                        value={selectedAccountPayer}
                        onChange={handleAccountPayerChange}
                        onBlur={handleBlur}
                        options={AccountPayerOptions}
                        isDisabled={!isEditable}
                        maxMenuHeight={175}
                        placeholder="Select account payer"
                      />
                      {errors.custentity_aie_nature_of_sponsor &&
                      touched.custentity_aie_nature_of_sponsor ? (
                        <span className="exclamation">
                          <i className="fal fa-exclamation-circle"></i>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>First Name <span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      name="firstname"
                      className={
                        "form-control input-match-select" +
                        (errors.firstname && touched.firstname ? " is-invalid" : "")
                      }
                      value={values.firstname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.firstname && touched.firstname ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Last Name <span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      name="lastname"
                      className={
                        "form-control input-match-select" +
                        (errors.lastname && touched.lastname ? " is-invalid" : "")
                      }
                      value={values.lastname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.lastname && touched.lastname ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>ID/Passport Number <span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      name="custentity_bb1_contact_id_num"
                      className={
                        "form-control input-match-select" +
                        (errors.custentity_bb1_contact_id_num &&
                        touched.custentity_bb1_contact_id_num
                          ? " is-invalid"
                          : "")
                      }
                      value={values.custentity_bb1_contact_id_num || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.custentity_bb1_contact_id_num &&
                    touched.custentity_bb1_contact_id_num ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Email Address <span className="required-asterisk">*</span></label>
                    <input
                      type="email"
                      name="email"
                      className={
                        "form-control input-match-select" +
                        (errors.email && touched.email ? " is-invalid" : "")
                      }
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.email && touched.email ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Phone Number <span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      name="phone"
                      className={
                        "form-control input-match-select" +
                        (errors.phone && touched.phone ? " is-invalid" : "")
                      }
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.phone && touched.phone ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {values.custentity_aie_nature_of_sponsor === 8 && (
                <>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Company Name <span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          name="custentity_aie_le_name"
                          className={
                            "form-control input-match-select" +
                            (errors.custentity_aie_le_name &&
                            touched.custentity_aie_le_name
                              ? " is-invalid"
                              : "")
                          }
                          value={values.custentity_aie_le_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isEditable}
                        />
                        {errors.custentity_aie_le_name &&
                        touched.custentity_aie_le_name ? (
                          <span className="exclamation">
                            <i className="fal fa-exclamation-circle"></i>
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Company Registration Number <span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          name="custentity_aie_le_reg_nr"
                          className={
                            "form-control input-match-select" +
                            (errors.custentity_aie_le_reg_nr &&
                            touched.custentity_aie_le_reg_nr
                              ? " is-invalid"
                              : "")
                          }
                          value={values.custentity_aie_le_reg_nr}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isEditable}
                        />
                        {errors.custentity_aie_le_reg_nr &&
                        touched.custentity_aie_le_reg_nr ? (
                          <span className="exclamation">
                            <i className="fal fa-exclamation-circle"></i>
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Company Representative <span className="required-asterisk">*</span></label>
                        <input
                          type="text"
                          name="custentity_aie_le_representative"
                          className={
                            "form-control input-match-select" +
                            (errors.custentity_aie_le_representative &&
                            touched.custentity_aie_le_representative
                              ? " is-invalid"
                              : "")
                          }
                          value={values.custentity_aie_le_representative}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isEditable}
                        />
                        {errors.custentity_aie_le_representative &&
                        touched.custentity_aie_le_representative ? (
                          <span className="exclamation">
                            <i className="fal fa-exclamation-circle"></i>
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="row mb-2">
                <div className="col-md-12">
                  <div className="form-group">
                    <label>Billing Address <span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      name="billingaddr1"
                      className={
                        "form-control input-match-select" +
                        (errors.billingaddr1 && touched.billingaddr1 ? " is-invalid" : "")
                      }
                      value={values.billingaddr1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.billingaddr1 && touched.billingaddr1 ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>City <span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      name="billingcity"
                      className={
                        "form-control input-match-select" +
                        (errors.billingcity && touched.billingcity ? " is-invalid" : "")
                      }
                      value={values.billingcity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.billingcity && touched.billingcity ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Postal Code <span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      name="billingzip"
                      className={
                        "form-control input-match-select" +
                        (errors.billingzip && touched.billingzip ? " is-invalid" : "")
                      }
                      value={values.billingzip}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!isEditable}
                    />
                    {errors.billingzip && touched.billingzip ? (
                      <span className="exclamation">
                        <i className="fal fa-exclamation-circle"></i>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Country <span className="required-asterisk">*</span></label>
                    <div className="form-icon-group">
                      <Select
                        className={
                          "form-control custom-select-box acc-payer" +
                          (errors.billingcountry && touched.billingcountry ? " is-invalid" : "")
                        }
                        name="billingcountry"
                        value={selectedCountry}
                        onChange={(option) => {
                          setFieldValue("billingcountry", option ? option.value : "");
                        }}
                        onBlur={handleBlur}
                        options={COUNTRY_LIST}
                        isDisabled={!isEditable}
                        maxMenuHeight={175}
                        placeholder="Select country"
                      />
                      {errors.billingcountry && touched.billingcountry ? (
                        <span className="exclamation">
                          <i className="fal fa-exclamation-circle"></i>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              <div className="row mt-2">
                <div className="col-md-12">
                  <SectionTitle>Payment Details</SectionTitle>
                  <div className="form-group" title="How will your account be settled?">
                    <label>How will your account be settled?</label>
                    <div className="form-icon-group">
                      <Select
                        className={
                          "form-control custom-select-box acc-payer" +
                          (!isEditable ? " account-payer-select-disabled" : "")
                        }
                        name="payment_option"
                        value={selectedPaymentOption}
                        onChange={(option) => {
                          setFieldValue("payment_option", option ? option.value : "");
                        }}
                        onBlur={handleBlur}
                       options={PaymentOptionOptions}
                       isDisabled={!isPaymentOptionEditable}
                        maxMenuHeight={175}
                        placeholder="Select payment option"
                      />
                    </div>
                  </div>

                  <div className="mt-2">
                    <PaymentMethods studentNumber={studentNumber} />
                  </div>
                </div>
              </div>

              <div className="mt-4" style={{ width: "fit-content" }}>
                <ButtonDoneEditing
                  type="submit"
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting ? (
                    <i className="fas fa-cog fa-spin"></i>
                  ) : (
                    <i className="fa-light fa-circle-check"></i>
                  )}
                  <span>
                    {currentSponsorPaymentMethod && 
                     currentSponsorPaymentMethod !== "" && 
                     accountPayerUpdatedStatus === 1
                      ? "Update"
                      : "Submit"}
                  </span>
                </ButtonDoneEditing>
              </div>
            </form>
          </FormLoadingWrapper>
        </SectionContainer>
      </ApplicationLayout>
    </Container>
  );
}
